import {
  AddressingType,
  ApartmentDTO,
  ApartmentInvitationDTO,
  ApartmentInvitationStatusEnum,
  BuildingDTO,
  EntrancePlantDetailDTO,
} from '@/../client-generator/generated-clients/plants';
import plantsClient from '@/core/services/plants';
import { SET_APP_LOADING } from '@/core/store/action-types';
import { getReferenceDeviceForVirtualDevice, loadRisersFloorsApartments, processCsvDataMassiveInvitation } from '@/core/utils/EntrancePlant/entrance-plant.utils';
import { Options, Vue } from 'vue-class-component';
import { mapState } from 'vuex';

interface EmailInput {
  email: string;
  isEnable: boolean;
  isValid: boolean;
}

@Options({
  name: 'DialogMassiveInviteResidents',
  props: {
    isVisible: {
      type: Boolean,
      required: true,
    },
  },
  computed: {
    ...mapState(['currentEntrancePlant']),
  },
  watch: {
    isVisible: function (newValue) {
      this.isDialogVisible = newValue;
      if (this.isDialogVisible) {
        this.checkApartmentsInvitePossibility();
        this.initInvitations();
      }
    },
  },
  components: {},
  emits: ['hide-dialog', 'update-list'],
})
export default class DialogMassiveInviteResidents extends Vue {
  currentEntrancePlant!: EntrancePlantDetailDTO;
  isVisible!: boolean;
  isDialogVisible = false;
  invites: ApartmentInvitationDTO[] = [];
  emailInput: { [key: string]: EmailInput } = {};

  AddressingType = AddressingType;

  showImportSection: boolean = false;
  importedData: ApartmentInvitationDTO[] = [];

  buildingStructure(building: BuildingDTO) {
    return loadRisersFloorsApartments(building);
  }

  checkMailValid(apartmentId: string) {
    this.emailInput[apartmentId].isValid = this.validateEmail(
      this.emailInput[apartmentId].email
    );
  }

  validateEmail(email?: string) {
    if (!email || email.trim() === '') {
      return true;
    }
    const re = /\S+@\S+/;
    return re.test(email);
  }

  getErrorLabel(apartmentId: string) {
    if (!this.emailInput[apartmentId].isValid) {
      return this.$t('AppResourceEP.InputNotValid');
    } else {
      return '';
    }
  }

  mounted() {
    this.isDialogVisible = this.isVisible;
  }

  handleHideDialog() {
    this.showImportSection = false;
    this.isDialogVisible = false;
    this.importedData = [];
    this.invites = [];
  }

  doHideDialog() {
    this.showImportSection = false;
    this.invites = [];
    this.importedData = [];
    this.$emit('hide-dialog');
  }

  triggerFilePicker() {
    const input = document.getElementById('fileInput') as HTMLInputElement;
    input.value = '';
    input.click();
  }

  checkApartmentsInvitePossibility() {
    const apartments = this.currentEntrancePlant.buildings?.map(b => b.apartments).filter(a => a != null).flat();
    const activeGates = this.currentEntrancePlant.gates?.filter(g => getReferenceDeviceForVirtualDevice(g)?.isCommissioned) ?? [];
    if (!apartments) return;
    apartments.forEach((apt) => {
      if(!apt) return;
      apt.canInvite = this.currentEntrancePlant.syncStatus?.isPlantActive 
        && this.currentEntrancePlant.syncStatus.areVirtualDevicesSentToEliot
        && activeGates.some(g => g.virtualDevicesApt?.includes(apt.apartmentId!))
        && this.currentEntrancePlant.apartmentInvitations?.every(inv => inv.apartmentId !== apt.apartmentId);
    });
  }

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) {
      return;
    }

    const file = input.files[0];
    if (!this.fileIsCsv(file)) {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('importFile.error'),
        closable: true,
        life: 6000,
      });
      console.error('File is not a CSV!');
      return;
    }    
    this.$store.dispatch('SET_APP_LOADING', true);
    const reader = new FileReader();

    reader.onload = async (e: ProgressEvent<FileReader>) => {
      let text = e.target?.result;
      this.$store.dispatch('SET_APP_LOADING', false);
      if (typeof text !== 'string') {
        return;
      } else {
        if(!text.includes(',')) text = text.replaceAll(';', ',');
        const data = await processCsvDataMassiveInvitation(
          text,
          this.currentEntrancePlant
        );
        if (data.length === 0) {
          this.$toast.add({
            severity: 'error',
            summary: this.$t('importFile.error'),
            closable: true,
            life: 6000,
          });
        }

        this.importedData = data;

        this.invites.push(...this.importedData);
        this.initInvitations();

        this.showImportSection = false;
      }
    };

    reader.onerror = (e) => {
      this.$toast.add({
        severity: 'error',
        summary: this.$t('importFile.error'),
        closable: true,
        life: 6000,
      });
    };
    reader.readAsText(file);
  }

  fileIsCsv(file: File) {
    return (file.type === 'text/csv' || file.type === 'application/vnd.ms-excel') && file.name.endsWith('.csv');
  }

  tabBuildingLabel(building: BuildingDTO) {
    const apartCount = building.apartments
      ? `(${building.apartments.length})`
      : '';
    return `${building.name} ${apartCount}`;
  }

  apartmentInputLabel(apartment: ApartmentDTO) {
    switch (this.currentEntrancePlant.addressingType) {
      case AddressingType.DirectCall_NumbersOnly:
      case AddressingType.DirectCall_Alphanumeric:
        return this.apartmentInputLabelCallingCode(apartment);
      case AddressingType.BuildingRiserFloorApartment:
        return this.apartmentInputLabelBrfa(apartment);
      default:
        return this.apartmentInputLabelDefault(apartment);
    }
  }

  apartmentInputLabelDefault(apartment: ApartmentDTO) {
    let baseLabel = `${apartment.name}`; 
    if (apartment.labels && apartment.labels?.length > 0) {
      const label = apartment.labels[0];
      if (label.isPrivate && (label.surname || label.name)) {
        baseLabel += ' -';
        baseLabel += ` ${label.surname ?? ''}`;
        baseLabel += ` ${label.name ?? ''}`;
      } else if (!label.isPrivate && label.companyName) {
        baseLabel += ` - ${label.companyName}`;
      }
      return baseLabel;
    } else {
      return baseLabel;
    }  
  }

  apartmentInputLabelBrfa(apartment: ApartmentDTO) {
    return `${apartment.code?.replaceAll('|', ' ')}`;
  }

  apartmentInputLabelCallingCode(apartment: ApartmentDTO) {
    let baseLabel = `${apartment.name} - ${apartment.code}`;
    if (apartment.labels && apartment.labels?.length > 0) {
      const label = apartment.labels[0];
      if (label.isPrivate && (label.surname || label.name)) {
        baseLabel += ` |`;
        baseLabel += ` ${label.surname ?? ''}`;
        baseLabel += ` ${label.name ?? ''}`;
      } else if (!label.isPrivate && label.companyName) {
        baseLabel += ` | ${label.companyName}`;
      }
      return baseLabel;
    } else {
      return baseLabel;
    }
  }

  initInvitations() {
    if (this.currentEntrancePlant.apartmentInvitations)
      this.invites = [...this.currentEntrancePlant.apartmentInvitations];
    if (this.importedData) {
      this.invites.push(...this.importedData);
    }

    this.currentEntrancePlant.buildings?.forEach((building) =>
      building.apartments?.forEach((apartm) => {
        if (
          !this.invites.find(
            (invite) => invite.apartmentId === apartm.apartmentId
          )
        ) {
          this.invites.push(
            new ApartmentInvitationDTO({
              apartmentId: apartm.apartmentId,
              email: '',
              status: ApartmentInvitationStatusEnum.Pending,
            })
          );
        }
      })
    );

    for (const invite of this.invites) {
      if (invite.apartmentId !== undefined && invite.email !== undefined) {
        this.emailInput[invite.apartmentId] = {
          email: invite.email,
          isEnable: invite.email && !this.isImported(invite) ? false : true,
          isValid: this.validateEmail(invite.email),
        };
      }
    }
  }

  disableInput(apartment: ApartmentDTO) {
    if (apartment.apartmentId === undefined) return true;
    return !this.emailInput[apartment.apartmentId].isEnable || !apartment.canInvite;
  }

  inPending(apartmentId: string) {
    if (this.invites.find((invite) => invite.apartmentId === apartmentId)) {
      return (
        this.invites.find((invite) => invite.apartmentId === apartmentId)
          ?.status === ApartmentInvitationStatusEnum.Pending
      );
    }
  }

  async handleButtonSubmitAction() {
    await this.sendInvites();
    
    this.handleHideDialog();
  }

  isImported(invite: ApartmentInvitationDTO) {
    return (
      this.importedData.find((i) => i.apartmentId === invite.apartmentId) !==
      undefined
    );
  }

  handleExportImportProcedure() {
    this.showImportSection = !this.showImportSection;
  }

  async handleExportClick() {
    try {
      this.$store.dispatch(SET_APP_LOADING, true);
      const csv = await plantsClient.invitationCsv(
        this.currentEntrancePlant.desktopId!,
        undefined
      );

      if (csv.data) {
        const url = window.URL.createObjectURL(new Blob([csv.data]));
        const link = document.createElement('a');
        link.href = url;
        const fileName = this.currentEntrancePlant.name?.trim().replaceAll(' ', '_')+ '_invitation' + '.csv'
        link.setAttribute('download', fileName);
        document.body.appendChild(link);
        link.click();
        link.parentNode!.removeChild(link);
      }
    } catch (error) {
      switch (error.status) {
        case 423:
          this.$router.push({
            name: 'page-plants-hub',
          });
          break;
      }
      console.log(error);
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }

  async sendInvites() {
    if (this.currentEntrancePlant.desktopId === undefined) return;
    this.$store.dispatch(SET_APP_LOADING, true);
    try {
      const newInvites: ApartmentInvitationDTO[] = [];

      this.invites.forEach((invite) => {
        if (invite.apartmentId && this.emailInput[invite.apartmentId].isEnable)
          newInvites.push(
            new ApartmentInvitationDTO({
              apartmentId: invite.apartmentId,
              email: this.emailInput[invite.apartmentId].email.trim(),
              status: ApartmentInvitationStatusEnum.Pending,
            })
          );
      });

      await plantsClient.massiveinvitation(
        this.currentEntrancePlant.desktopId,
        newInvites
      );

      this.$emit('update-list');
      this.$toast.add({
        severity: 'success',
        summary: this.$t('AppResourceEP.InvitationsSent'),
        closable: false,
        life: 3000,
      });
    
    } catch (error) {
      console.log(error);
      this.$toast.add({
        severity: 'error',
        summary: 'Error',
        closable: false,
        life: 3000,
      });
    } finally {
      this.$store.dispatch(SET_APP_LOADING, false);
    }
  }
}
